import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api, refreshFileToken, clearFileCache } from "@/lib/api";
import { setActiveCurrency } from "@/lib/format";
import { storage } from "@/lib/storage";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionChanged, setSessionChanged] = useState(false);
  // The user id this browser tab was loaded with. Used to detect a different
  // account being logged in from another tab/window (cross-session leak guard).
  const sessionUidRef = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setActiveCurrency(data.company?.currency || "CZK");  // měna firmy pro formátování částek
      setUser(data.user);
      setCompany(data.company);
      setAccess(data.access || null);
      sessionUidRef.current = data.user?.id || null;
      // pp-uid is NOT a credential — just an id used to detect another account
      // logging in in a second tab. The token stays in the httpOnly cookie.
      storage.set("pp-uid", data.user?.id || "");
      await refreshFileToken();
    } catch {
      setUser(false);
      setCompany(null);
      setAccess(null);
      sessionUidRef.current = null;
      localStorage.removeItem("pp-uid");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(() => {
      if (sessionUidRef.current) refreshFileToken(true);
    }, 8 * 60 * 1000);
    const onVis = () => {
      if (document.visibilityState === "visible" && sessionUidRef.current) refreshFileToken();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect that a DIFFERENT user was authenticated in this browser (another
  // tab logged in/out, or the token was swapped). Token rotation for the same
  // user keeps the same `sub`, so it does not trigger a false positive.
  useEffect(() => {
    const isDifferentAccount = () => {
      if (sessionUidRef.current == null) return false; // not yet loaded / logged out here
      const uid = storage.get("pp-uid") || null;
      return uid !== sessionUidRef.current;
    };
    const onStorage = (e) => {
      if (e.key && e.key !== "pp-uid") return;
      if (isDifferentAccount()) setSessionChanged(true);
    };
    const onFocus = () => { if (isDifferentAccount()) setSessionChanged(true); };
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  // Force a full reload once a foreign session is detected — wipes all React
  // state + react-query cache so no stale data from the old account survives.
  useEffect(() => {
    if (!sessionChanged) return;
    const t = setTimeout(() => window.location.assign("/"), 1500);
    return () => clearTimeout(t);
  }, [sessionChanged]);

  // New login: hard reset the whole client. A full page load to "/" destroys
  // every cached state store (React + react-query) from the previous session
  // and re-bootstraps auth for the new role from scratch.
  const applyAuth = async (data) => {
    await clearFileCache();
    storage.set("pp-uid", data.user?.id || "");
    window.location.assign("/");
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    await clearFileCache();   // na zařízení nesmí zůstat fotky odhlášeného účtu
    storage.remove("pp-uid");
    sessionUidRef.current = null;
    window.location.assign("/login");
  };

  if (sessionChanged) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="session-changed-overlay">
        <div className="max-w-sm text-center bg-card border border-border rounded-[20px] p-8 shadow-2xl">
          <div className="text-2xl font-bold tracking-tight mb-2">Proyekty</div>
          <p className="font-semibold">Přihlášen jiný uživatel</p>
          <p className="text-sm text-muted-foreground mt-1">V jiném okně se přihlásil jiný účet. Z bezpečnostních důvodů obnovujeme stránku…</p>
          <button onClick={() => window.location.assign("/")} data-testid="session-reload-btn"
            className="mt-5 w-full h-11 rounded-[14px] font-bold bg-brand text-white hover:bg-brand/90 transition-colors">
            Obnovit nyní
          </button>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, company, access, loading, applyAuth, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export const isManager = (u) => u && (u.role === "OWNER" || u.role === "MANAGER" || u.role === "ASSISTANT");
export const isSubcontractor = (u) => u && u.role === "SUBCONTRACTOR";
// Projektant (173): jen svoje zakázky; čte harmonogram a Gantt, píše deník, úkoly jako vedení.
// ZÁMĚRNĚ není „manager" — ten odemyká peníze, tým, sklad a úpravy zakázek.
export const isDesigner = (u) => u && u.role === "DESIGNER";
// Práva vedení nad úkoly (zadávat komukoli, schvalovat, vracet).
export const canLeadTasks = (u) => isManager(u) || isDesigner(u);
// Účetní ZÁMĚRNĚ není „manager" — ten odemyká Tým, Sklad, Deník i Plánování.
export const isAccountant = (u) => u && u.role === "ACCOUNTANT";
// Kdo smí na /sklad. isManager ZÁMĚRNĚ nerozšiřujeme (odemyká Tým/Deník/Plánování).
export const canWarehouse = (u) => u && ["OWNER", "MANAGER", "ASSISTANT", "WAREHOUSE"].includes(u.role);
